import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Fab,
  makeStyles,
  useTheme
} from "@material-ui/core";
import {
  Mic,
  Stop,
  CallEnd,
  VolumeUp,
  VolumeOff
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import WaveformVisualization from "../WaveformVisualization";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1)
  },
  controlsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    width: "100%"
  },
  mainButton: {
    width: 72,
    height: 72,
    boxShadow: theme.shadows[4],
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      boxShadow: theme.shadows[8]
    },
    "&:active": {
      transform: "scale(0.95)"
    }
  },
  recordingButton: {
    animation: "$pulse 2s infinite"
  },
  "@keyframes pulse": {
    "0%": {
      transform: "scale(1)",
      boxShadow: theme.shadows[4]
    },
    "50%": {
      transform: "scale(1.05)",
      boxShadow: theme.shadows[8]
    },
    "100%": {
      transform: "scale(1)",
      boxShadow: theme.shadows[4]
    }
  },
  secondaryButton: {
    width: 48,
    height: 48
  },
  statusText: {
    textAlign: "center",
    minHeight: 24
  },
  waveformContainer: {
    width: "100%",
    maxWidth: 400,
    height: 60,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper
  },
  recordingIndicator: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.error.main
  },
  processingIndicator: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.warning.main
  },
  speakingIndicator: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.success.main
  },
  volumeSlider: {
    width: 120
  }
}));

const VoiceStreamingControls = ({
  isRecording,
  isProcessing,
  isSpeaking,
  isConnected,
  onRecordingStateChange,
  onAudioProcessed,
  onEndSession
}) => {
  const classes = useStyles();
  const theme = useTheme();
  
  // State
  const [hasPermission, setHasPermission] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  // Refs
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const animationRef = useRef(null);
  const chunksRef = useRef([]);

  // Initialize audio context and get permissions
  const initializeAudio = useCallback(async () => {
    try {
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000
        }
      });

      streamRef.current = stream;
      setHasPermission(true);

      // Setup audio context for visualization
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      
      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.8;
      source.connect(analyserRef.current);

      // Setup media recorder
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus"
      });

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(chunksRef.current, { type: "audio/webm" });
        onAudioProcessed(audioBlob);
        chunksRef.current = [];
      };

    } catch (error) {
      console.error("Error initializing audio:", error);
      setHasPermission(false);
      toastError({ 
        message: i18n.t("voiceAssistant.streaming.microphonePermission") 
      });
    }
  }, [onAudioProcessed]);

  // Start audio level monitoring
  const startAudioLevelMonitoring = useCallback(() => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    
    const updateLevel = () => {
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
      setAudioLevel(average / 255);
      
      if (isRecording || isSpeaking) {
        animationRef.current = requestAnimationFrame(updateLevel);
      }
    };

    updateLevel();
  }, [isRecording, isSpeaking]);

  // Stop audio level monitoring
  const stopAudioLevelMonitoring = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    setAudioLevel(0);
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    if (!hasPermission) {
      await initializeAudio();
      return;
    }

    if (!mediaRecorderRef.current || !isConnected) return;

    try {
      chunksRef.current = [];
      mediaRecorderRef.current.start();
      onRecordingStateChange(true);
      startAudioLevelMonitoring();
    } catch (error) {
      console.error("Error starting recording:", error);
      toastError(error);
    }
  }, [hasPermission, isConnected, initializeAudio, onRecordingStateChange, startAudioLevelMonitoring]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (!mediaRecorderRef.current) return;

    try {
      mediaRecorderRef.current.stop();
      onRecordingStateChange(false);
      stopAudioLevelMonitoring();
    } catch (error) {
      console.error("Error stopping recording:", error);
      toastError(error);
    }
  }, [onRecordingStateChange, stopAudioLevelMonitoring]);

  // Toggle recording
  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  // Get main button props
  const getMainButtonProps = useCallback(() => {
    if (isProcessing) {
      return {
        color: "default",
        disabled: true,
        children: <CircularProgress size={32} />
      };
    }

    if (isSpeaking) {
      return {
        style: { backgroundColor: theme.palette.success.main, color: "white" },
        children: <VolumeUp fontSize="large" />
      };
    }

    if (isRecording) {
      return {
        style: { backgroundColor: theme.palette.error.main, color: "white" },
        children: <Stop fontSize="large" />,
        className: classes.recordingButton
      };
    }

    return {
      color: "primary",
      children: <Mic fontSize="large" />,
      disabled: !isConnected
    };
  }, [isProcessing, isSpeaking, isRecording, isConnected, theme, classes.recordingButton]);

  // Get status text
  const getStatusText = useCallback(() => {
    if (isProcessing) return i18n.t("voiceAssistant.streaming.processing");
    if (isSpeaking) return i18n.t("voiceAssistant.streaming.speaking");
    if (isRecording) return i18n.t("voiceAssistant.streaming.recording");
    if (!isConnected) return i18n.t("voiceAssistant.streaming.disconnected");
    return i18n.t("voiceAssistant.voiceInterface.tapToSpeak");
  }, [isProcessing, isSpeaking, isRecording, isConnected]);

  // Initialize on mount
  useEffect(() => {
    if (isConnected && !hasPermission) {
      initializeAudio();
    }
  }, [isConnected, hasPermission, initializeAudio]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAudioLevelMonitoring();
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close();
      }
    };
  }, [stopAudioLevelMonitoring]);

  return (
    <Box className={classes.root}>
      {/* Status Text */}
      <Typography variant="body2" className={classes.statusText}>
        {getStatusText()}
      </Typography>

      {/* Waveform Visualization */}
      <Box className={classes.waveformContainer}>
        <WaveformVisualization
          audioLevel={audioLevel}
          isRecording={isRecording}
          isSpeaking={isSpeaking}
          isProcessing={isProcessing}
        />
      </Box>

      {/* Main Controls */}
      <Box className={classes.controlsRow}>
        {/* Mute Button */}
        <Tooltip title={isMuted ? i18n.t("voiceAssistant.audioControls.unmute") : i18n.t("voiceAssistant.audioControls.mute")}>
          <IconButton
            className={classes.secondaryButton}
            onClick={toggleMute}
            color={isMuted ? "secondary" : "default"}
          >
            {isMuted ? <VolumeOff /> : <VolumeUp />}
          </IconButton>
        </Tooltip>

        {/* Main Record Button */}
        <Tooltip 
          title={
            isRecording 
              ? i18n.t("voiceAssistant.streaming.stopRecording")
              : i18n.t("voiceAssistant.streaming.startRecording")
          }
        >
          <Fab
            className={`${classes.mainButton} ${isRecording ? classes.recordingButton : ""}`}
            onClick={toggleRecording}
            {...getMainButtonProps()}
          />
        </Tooltip>

        {/* End Session Button */}
        <Tooltip title={i18n.t("voiceAssistant.streaming.endSession")}>
          <IconButton
            className={classes.secondaryButton}
            onClick={onEndSession}
            color="secondary"
          >
            <CallEnd />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Status Indicators */}
      {isRecording && (
        <Box className={classes.recordingIndicator}>
          <Box
            width={8}
            height={8}
            borderRadius="50%"
            bgcolor="error.main"
            sx={{ animation: "blink 1s infinite" }}
          />
          <Typography variant="caption">
            {i18n.t("voiceAssistant.streaming.recording")}
          </Typography>
        </Box>
      )}

      {isProcessing && (
        <Box className={classes.processingIndicator}>
          <CircularProgress size={16} color="inherit" />
          <Typography variant="caption">
            {i18n.t("voiceAssistant.streaming.processing")}
          </Typography>
        </Box>
      )}

      {isSpeaking && (
        <Box className={classes.speakingIndicator}>
          <VolumeUp fontSize="small" />
          <Typography variant="caption">
            {i18n.t("voiceAssistant.streaming.speaking")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default VoiceStreamingControls;